import React, { Suspense, lazy, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "./App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ProCartProvider } from "./context/ProCartContext";
import { SettingsProvider } from "./context/SettingsContext";
import { CompareProvider } from "./context/CompareContext";
import CompareBar from "./components/CompareBar";
import MobileBottomNav from "./components/MobileBottomNav";
import { Toaster } from "./components/ui/sonner";
import { PageTransition } from "./components/fx/PageTransition";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { CursorGlow, ScrollProgress } from "./components/fx/Ambient";
import { AnnouncementBar, MaintenanceGate } from "./components/SiteNotices";
import HomePage from "./pages/HomePage";
import CategoryPage from "./pages/CategoryPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import AuthPage from "./pages/AuthPage";
import AccountPage from "./pages/AccountPage";
const VehicleSearchPage = lazy(() => import("./pages/VehicleSearchPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CarteGrisePage = lazy(() => import("./pages/CarteGrisePage"));
const PneumatiquesPage = lazy(() => import("./pages/PneumatiquesPage"));
const PerformancePage = lazy(() => import("./pages/PerformancePage"));
import AboutPage from "./pages/AboutPage";
import ShippingPage from "./pages/ShippingPage";
import TermsPage from "./pages/TermsPage";
import LegalPage from "./pages/LegalPage";
import ContactPage from "./pages/ContactPage";
import AidePage from "./pages/AidePage";
import ProfessionnelsPage from "./pages/ProfessionnelsPage";
const ProConditionsPage = lazy(() => import("./pages/ProConditionsPage"));
const ProEspacePage = lazy(() => import("./pages/ProEspacePage"));
const InvoicePage = lazy(() => import("./pages/InvoicePage"));
const QuotePage = lazy(() => import("./pages/QuotePage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ComptoirPage = lazy(() => import("./pages/ComptoirPage"));
const PreparationPage = lazy(() => import("./pages/PreparationPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-[#08090C]"><Loader2 className="animate-spin text-[#FF3B3B]" size={36} /></div>
);

const SiteLayout = () => (
  <MaintenanceGate>
    <ScrollToTop />
    <ScrollProgress />
    <CursorGlow />
    <AnnouncementBar />
    <Header />
    <main className="flex-1">
      <Suspense fallback={<PageLoader />}>
        <PageTransition><Outlet /></PageTransition>
      </Suspense>
    </main>
    <Footer />
    <CompareBar />
    <MobileBottomNav />
  </MaintenanceGate>
);

function App() {
  return (
    <div className="App min-h-screen flex flex-col bg-[#08090C]">
      <Toaster position="top-right" richColors closeButton theme="dark" />
      <BrowserRouter>
        <SettingsProvider>
        <AuthProvider>
          <CartProvider>
            <ProCartProvider>
            <CompareProvider>
            <Routes>
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminPage /></Suspense>} />
              <Route path="/espace-pro" element={<Suspense fallback={<PageLoader />}><ProEspacePage /></Suspense>} />
              <Route path="/comptoir" element={<Suspense fallback={<PageLoader />}><ComptoirPage /></Suspense>} />
              <Route path="/preparation" element={<Suspense fallback={<PageLoader />}><PreparationPage /></Suspense>} />
              <Route path="/facture/:orderId" element={<Suspense fallback={<PageLoader />}><InvoicePage /></Suspense>} />
              <Route path="/devis/comptoir/:posQuoteId" element={<Suspense fallback={<PageLoader />}><QuotePage /></Suspense>} />
              <Route path="/devis/public/:token" element={<Suspense fallback={<PageLoader />}><QuotePage /></Suspense>} />
              <Route path="/devis/:quoteId" element={<Suspense fallback={<PageLoader />}><QuotePage /></Suspense>} />
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/categorie/:slug" element={<CategoryPage />} />
                <Route path="/recherche" element={<CategoryPage />} />
                <Route path="/produit/:id" element={<ProductPage />} />
                <Route path="/panier" element={<CartPage />} />
                <Route path="/comparer" element={<Suspense fallback={<PageLoader />}><ComparePage /></Suspense>} />
                <Route path="/paiement/succes" element={<PaymentSuccessPage />} />
                <Route path="/paiement/annule" element={<Navigate to="/panier?paiement=annule" replace />} />
                <Route path="/connexion" element={<AuthPage />} />
                <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/compte" element={<AccountPage />} />
                <Route path="/vehicule" element={<VehicleSearchPage />} />
                <Route path="/carte-grise" element={<CarteGrisePage />} />
                <Route path="/pneumatiques" element={<PneumatiquesPage />} />
                <Route path="/performance" element={<PerformancePage />} />
                <Route path="/a-propos" element={<AboutPage />} />
                <Route path="/livraison-retours" element={<ShippingPage />} />
                <Route path="/conditions-generales" element={<TermsPage />} />
                <Route path="/mentions-legales" element={<LegalPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/aide" element={<AidePage />} />
                <Route path="/professionnels" element={<ProfessionnelsPage />} />
                <Route path="/professionnels/conditions" element={<ProConditionsPage />} />
              </Route>
            </Routes>
            </CompareProvider>
            </ProCartProvider>
          </CartProvider>
        </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
